// A rolling-window limiter for POST /binders/preview (server cap: 10 per
// minute per owner). At most `max` sends in any `windowMs`, and nothing at
// all before a server Retry-After has passed (honored in full, not capped).
// A waiter that is aborted (TanStack cancels a preview query whose rules are
// no longer the newest) leaves without taking a slot, so the queue always
// coalesces to the rules on screen.

import { CopiesError } from '../api/collectionCopies'
import { ERR } from '../api/collectionCopies.codes'

type Sleep = (ms: number, signal?: AbortSignal) => Promise<void>

const abortError = () => new CopiesError(ERR.aborted)

const realSleep: Sleep = (ms, signal) => new Promise<void>((resolve, reject) => {
  if (signal?.aborted) { reject(abortError()); return }
  // eslint-disable-next-line i18next/no-literal-string -- DOM event name
  const t = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve() }, ms)
  const onAbort = () => { clearTimeout(t); reject(abortError()) }
  // eslint-disable-next-line i18next/no-literal-string -- DOM event name
  signal?.addEventListener('abort', onAbort, { once: true })
})

export interface Limiter {
  /** Wait for a slot (rejects when `signal` aborts), then take it. */
  acquire: (signal?: AbortSignal) => Promise<void>
  /** The server said wait: nothing goes out before `ms` from now. */
  blockFor: (ms: number) => void
  reset: () => void
}

export function createLimiter(opts: { max: number; windowMs: number; now?: () => number; sleep?: Sleep }): Limiter {
  const now = opts.now ?? (() => Date.now())
  const sleep = opts.sleep ?? realSleep
  let sends: number[] = []
  let blockedUntil = 0
  return {
    acquire: async signal => {
      for (;;) {
        if (signal?.aborted) throw abortError()
        const t = now()
        sends = sends.filter(s => t - s < opts.windowMs)
        const windowWait = sends.length >= opts.max ? sends[0] + opts.windowMs - t : 0
        const wait = Math.max(windowWait, blockedUntil - t)
        if (wait <= 0) {
          sends.push(t)
          return
        }
        await sleep(wait, signal)
      }
    },
    blockFor: ms => { blockedUntil = Math.max(blockedUntil, now() + Math.max(0, ms)) },
    reset: () => { sends = []; blockedUntil = 0 },
  }
}

/** Retry-After as ms (delta-seconds or an HTTP date), NOT capped; null when absent or unreadable. */
export function retryAfterUncapped(header: string | null | undefined, now = Date.now()): number | null {
  if (!header) return null
  const t = header.trim()
  if (/^\d+$/.test(t)) return parseInt(t, 10) * 1000
  const at = Date.parse(t)
  return Number.isNaN(at) ? null : Math.max(0, at - now)
}

/** One window under the server's 10 per minute. */
export const PREVIEW_MAX_PER_WINDOW = 9
export const PREVIEW_WINDOW_MS = 60_000
/** A 429 without a readable Retry-After waits out a whole window. */
export const PREVIEW_DEFAULT_BLOCK_MS = 60_000

export const previewLimiter = createLimiter({ max: PREVIEW_MAX_PER_WINDOW, windowMs: PREVIEW_WINDOW_MS })
